import { useState, useEffect, useRef, useCallback } from 'react';

export const useCodeEditorResizing = (containerRef) => {
  const [isResizingHorizontal, setIsResizingHorizontal] = useState(false);
  const [isResizingVertical, setIsResizingVertical] = useState(false);
  const [isResizingCorner, setIsResizingCorner] = useState(false);
  const [outputPaneWidth, setOutputPaneWidth] = useState(40); // Percentage for right placement
  const [outputPaneHeight, setOutputPaneHeight] = useState(300); // Pixels for bottom placement

  // Track last resize time to throttle updates
  const lastResizeTimeRef = useRef(0);
  const pendingResizeRef = useRef(null);
  const THROTTLE_MS = 16; // ~60fps, smooth but not excessive

  // Throttled state update function
  const throttledSetSize = useCallback((widthUpdate, heightUpdate) => {
    const now = Date.now();
    const timeSinceLastResize = now - lastResizeTimeRef.current;

    if (timeSinceLastResize >= THROTTLE_MS) {
      // Update immediately
      lastResizeTimeRef.current = now;
      if (widthUpdate !== null) setOutputPaneWidth(widthUpdate);
      if (heightUpdate !== null) setOutputPaneHeight(heightUpdate);
      pendingResizeRef.current = null;
    } else {
      // Schedule update for later
      if (pendingResizeRef.current) {
        clearTimeout(pendingResizeRef.current);
      }

      pendingResizeRef.current = setTimeout(() => {
        lastResizeTimeRef.current = Date.now();
        if (widthUpdate !== null) setOutputPaneWidth(widthUpdate);
        if (heightUpdate !== null) setOutputPaneHeight(heightUpdate);
        pendingResizeRef.current = null;
      }, THROTTLE_MS - timeSinceLastResize);
    }
  }, []);

  // Mouse handling functions for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizingCorner && containerRef.current) {
        // Handle corner resize - both horizontal and vertical simultaneously
        const containerRect = containerRef.current.getBoundingClientRect();

        // Horizontal resize
        const newWidthPixels = containerRect.right - e.clientX;
        const newWidth = (newWidthPixels / containerRect.width) * 100;
        const minOutputWidth = 20;
        const maxOutputWidth = 70;
        const constrainedWidth = Math.max(minOutputWidth, Math.min(maxOutputWidth, newWidth));

        // Vertical resize
        const newHeight = containerRect.bottom - e.clientY;
        const constrainedHeight = Math.max(200, Math.min(600, newHeight));

        // Throttled update for both dimensions
        throttledSetSize(constrainedWidth, constrainedHeight);

      } else if (isResizingHorizontal && containerRef.current) {
        // Handle horizontal resize for right-side placement
        const containerRect = containerRef.current.getBoundingClientRect();
        const fileExplorerWidth = 256;

        const newWidthPixels = containerRect.right - e.clientX;
        const newWidth = (newWidthPixels / containerRect.width) * 100;

        const minOutputWidth = 20;
        const maxOutputWidth = Math.max(20, 70 - (fileExplorerWidth / containerRect.width) * 100);

        const constrainedWidth = Math.max(minOutputWidth, Math.min(maxOutputWidth, newWidth));

        // Throttled update for width only
        throttledSetSize(constrainedWidth, null);

      } else if (isResizingVertical && containerRef.current) {
        // Handle vertical resize for bottom placement
        const containerRect = containerRef.current.getBoundingClientRect();
        const newHeight = containerRect.bottom - e.clientY;
        const constrainedHeight = Math.max(200, Math.min(600, newHeight));

        // Throttled update for height only
        throttledSetSize(null, constrainedHeight);
      }
    };

    const handleMouseUp = () => {
      const wasResizing = isResizingHorizontal || isResizingVertical || isResizingCorner;

      setIsResizingHorizontal(false);
      setIsResizingVertical(false);
      setIsResizingCorner(false);

      // Flush any pending resize
      if (pendingResizeRef.current) {
        clearTimeout(pendingResizeRef.current);
        pendingResizeRef.current = null;
      }

      // Remove highlight classes when releasing corner resize
      if (isResizingCorner) {
        document.querySelector('.horizontal-resize-handle > div')?.classList.remove('bg-gray-400');
        document.querySelector('.vertical-resize-handle')?.classList.remove('bg-gray-400');
      }

      // Trigger one final Monaco layout after resize completes
      // Use a small delay to ensure automaticLayout is re-enabled first
      if (wasResizing && window.monaco?.editor) {
        setTimeout(() => {
          requestAnimationFrame(() => {
            window.monaco.editor.getEditors().forEach(editor => {
              try {
                editor.layout();
              } catch (e) {
                // Ignore harmless ResizeObserver errors
                if (!e.message?.includes('ResizeObserver')) {
                  console.error('Monaco layout error:', e);
                }
              }
            });
          });
        }, 100);
      }
    };

    if (isResizingHorizontal || isResizingVertical || isResizingCorner) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      if (isResizingCorner) {
        document.body.style.cursor = 'nwse-resize';
      } else {
        document.body.style.cursor = isResizingHorizontal ? 'col-resize' : 'row-resize';
      }
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      // Cleanup pending resize on unmount
      if (pendingResizeRef.current) {
        clearTimeout(pendingResizeRef.current);
      }
    };
  }, [isResizingHorizontal, isResizingVertical, isResizingCorner, containerRef, throttledSetSize]);

  const handleHorizontalResizeStart = (e) => {
    e.preventDefault();
    setIsResizingHorizontal(true);
  };

  const handleVerticalResizeStart = (e) => {
    e.preventDefault();
    setIsResizingVertical(true);
  };

  const handleCornerResizeStart = (e) => {
    e.preventDefault();
    setIsResizingCorner(true);
  };

  return {
    // State
    isResizingHorizontal,
    isResizingVertical,
    isResizingCorner,
    outputPaneWidth,
    outputPaneHeight,

    // Actions
    handleHorizontalResizeStart,
    handleVerticalResizeStart,
    handleCornerResizeStart,
    setOutputPaneWidth,
    setOutputPaneHeight
  };
};
